import React from 'react';
import {
  getCurrentLanguage,
  setLanguage,
  getTranslation,
  DEFAULT_LANGUAGE,
} from '../config/i18nConfig';

interface LanguageContextType {
  language: string;
  setLanguageCode: (code: string) => void;
  t: (key: string) => string;
  direction: 'ltr' | 'rtl';
}

const LanguageContext = React.createContext<LanguageContextType | undefined>(undefined);

const getDirection = (language: string): 'ltr' | 'rtl' => {
  if (language === 'ar') {
    return 'rtl';
  }
  return 'ltr';
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = React.memo(
  ({ children }) => {
    const [language, setLanguageState] = React.useState<string>(() => {
      if (typeof window === 'undefined') {
        return DEFAULT_LANGUAGE;
      }
      return getCurrentLanguage();
    });

    const handleSetLanguage = React.useCallback((code: string) => {
      if (setLanguage(code)) {
        setLanguageState(code);

        // Apply direction to document
        const direction = getDirection(code);
        if (typeof window !== 'undefined') {
          document.documentElement.dir = direction;
          document.documentElement.lang = code;
        }
      }
    }, []);

    const t = React.useCallback(
      (key: string): string => {
        return getTranslation(key, language);
      },
      [language]
    );

    const direction = getDirection(language);

    React.useEffect(() => {
      if (typeof window !== 'undefined') {
        document.documentElement.dir = direction;
        document.documentElement.lang = language;
      }
    }, [direction, language]);

    const value: LanguageContextType = {
      language,
      setLanguageCode: handleSetLanguage,
      t,
      direction,
    };

    return (
      <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
    );
  }
);

LanguageProvider.displayName = 'LanguageProvider';

export const useLanguage = (): LanguageContextType => {
  const context = React.useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
